package components.project

import it.neckar.commons.kotlin.js.LocalStorageKey
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.commons.kotlin.js.LocalStorageSupport
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.project.previews.ProjectQueryComponent
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.previews.ResolvedProjectPreview
import it.neckar.lizergy.model.project.previews.ServerProjectPreview
import it.neckar.lizergy.model.project.process.state.AdvanceInvoiceProcessStateEntry.AdvanceInvoiceProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementPreparationProcessStateEntry.AssemblyBasementPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementProcessStateEntry.AssemblyBasementProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyPortfolioProcessStateEntry.AssemblyPortfolioProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofPreparationProcessStateEntry.AssemblyRoofPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofProcessStateEntry.AssemblyRoofProcessStates
import it.neckar.lizergy.model.project.process.state.BlueprintAcquisitionProcessStateEntry.BlueprintAcquisitionProcessStates
import it.neckar.lizergy.model.project.process.state.BlueprintProcessStateEntry.BlueprintProcessStates
import it.neckar.lizergy.model.project.process.state.ConfigurationProcessStateEntry.ConfigurationProcessStates
import it.neckar.lizergy.model.project.process.state.DocumentationProcessStateEntry.DocumentationProcessStates
import it.neckar.lizergy.model.project.process.state.FinalAccountProcessStateEntry.FinalAccountProcessStates
import it.neckar.lizergy.model.project.process.state.FinishingUpProcessStateEntry.FinishingUpProcessStates
import it.neckar.lizergy.model.project.process.state.GridAssessmentProcessStateEntry.GridAssessmentProcessStates
import it.neckar.lizergy.model.project.process.state.LizergyProcessStates
import it.neckar.lizergy.model.project.process.state.LizergyProcessStatesBrowserSerializer
import it.neckar.lizergy.model.project.process.state.OrderSpecialMaterialProcessStateEntry.OrderSpecialMaterialProcessStates
import it.neckar.lizergy.model.project.process.state.PresentationProcessStateEntry.PresentationProcessStates
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.lizergy.model.project.process.state.QuoteConfirmationProcessStateEntry.QuoteConfirmationProcessStates
import it.neckar.lizergy.model.project.process.state.QuoteOfferProcessStateEntry.QuoteOfferProcessStates
import it.neckar.lizergy.model.project.process.state.StartupOperationsProcessStateEntry.StartupOperationsProcessStates
import it.neckar.lizergy.model.project.process.state.SwitchMeterBoxProcessStateEntry.SwitchMeterBoxProcessStates
import it.neckar.open.concurrent.debounce
import it.neckar.open.kotlin.lang.divFloor
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.router.*
import it.neckar.react.common.table.*
import it.neckar.user.UserLoginName
import kotlinx.serialization.builtins.serializer
import react.*
import react.dom.*
import router.useDocumentTitle
import services.UiActions
import services.http.ProjectQueryRequest
import services.http.ProjectQuerySortedBy
import services.http.ProjectQuerySorting
import store.hooks.useLoadProcessStates
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver

val ProjectsOverviewFromUrl: FC<Props> = fc("ProjectsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()

  useDocumentTitle(companyName, "Alle Projekte")

  h1("mb-3") { +"Alle Projekte" }

  ProjectsOverview {
    attrs {
      this.defaultFilteredBy = null
      this.processStatesToFilter = LizergyProcessStates.allProcessStates
      this.processStatesToHide = listOf(ProjectProcessStates.Archived, ProjectProcessStates.Paused)
      this.projectQueryComponent = null
      this.tableColumns = { ServerProjectPreviewsTable.homepageColumns(filteredProcessState = it, userResolver = userResolver, companyResolver = companyResolver) }
      this.sortedBy = SortedBy.SortedDescending
      this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
      this.includeArchived = true
      this.keyPrefix = LocalStorageKeyPrefix("FromStore")
    }
  }
}

internal val ProjectsOverview: FC<ProjectsOverview4Props> = fc("ProjectsOverview") { props ->
  val navigate = useNavigateUrl()
  val loggedInUser = useRequireLoggedInUser()
  val userResolver = useSelectUserResolver()
  val processStates = useLoadProcessStates()

  // prefix for local storage keys: every Page controls its own keys
  val keyPrefix = props::keyPrefix.safeGet()

  val forCompany: CompanyCode = LocalStorageSupport.loadFromLocalStorage(
    key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.companyCode}"),
    serializer = CompanyCode.serializer(),
  ) ?: loggedInUser.company.companyCode
  val processStatesToFilter = props::processStatesToFilter.safeGet()
  val processStatesToHide = props::processStatesToHide.safeGet()
  val tableColumns = props::tableColumns.safeGet()
  val sortedBy = props::sortedBy.safeGet()
  val projectQueryForUser = props::projectQueryForUser.safeGet()
  val includeArchived = props::includeArchived.safeGet()

  val filteredByCompanyCode: StateInstance<CompanyCode?> = useState(forCompany)

  // If the value is null, all projects shall be shown.
  val filteredByMaintainerName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.maintainer}"), serializer = UserLoginName.serializer()
    )
  )
  val filteredByEditorName: StateInstance<UserLoginName?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.editor}"), serializer = UserLoginName.serializer()
    )
  )

  val filteredByProjectState: StateInstance<LizergyProcessStates?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.processState}"), serializer = LizergyProcessStatesBrowserSerializer
    )
  )

  val projectQueryComponent = props::projectQueryComponent.safeGet() ?: when (filteredByProjectState.value) {
    is AdvanceInvoiceProcessStates -> ProjectQueryComponent.AdvanceInvoice
    is AssemblyBasementPreparationProcessStates -> ProjectQueryComponent.AssemblyBasement
    is AssemblyBasementProcessStates -> ProjectQueryComponent.AssemblyBasement
    is AssemblyPortfolioProcessStates -> ProjectQueryComponent.AssemblyPortfolio
    is AssemblyRoofPreparationProcessStates -> ProjectQueryComponent.AssemblyRoof
    is AssemblyRoofProcessStates -> ProjectQueryComponent.AssemblyRoof
    is BlueprintAcquisitionProcessStates -> ProjectQueryComponent.Blueprint
    is BlueprintProcessStates -> ProjectQueryComponent.Blueprint
    is ConfigurationProcessStates -> ProjectQueryComponent.CurrentConfiguration
    is DocumentationProcessStates -> ProjectQueryComponent.Documentation
    is FinalAccountProcessStates -> ProjectQueryComponent.FinalAccount
    is FinishingUpProcessStates -> ProjectQueryComponent.FinishingUp
    is GridAssessmentProcessStates -> ProjectQueryComponent.GridAssessment
    is OrderSpecialMaterialProcessStates -> ProjectQueryComponent.OrderSpecialMaterial
    is PresentationProcessStates -> ProjectQueryComponent.CurrentQuote
    is ProjectProcessStates -> ProjectQueryComponent.Project
    is QuoteConfirmationProcessStates -> ProjectQueryComponent.CurrentQuote
    is QuoteOfferProcessStates -> ProjectQueryComponent.CurrentQuote
    is StartupOperationsProcessStates -> ProjectQueryComponent.StartupOperations
    is SwitchMeterBoxProcessStates -> ProjectQueryComponent.SwitchMeterBox
    null -> null
  }

  val filterInputStateProject = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterProject}"), serializer = String.serializer()) ?: ""
  )

  val filterInputStateAddress = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.filterAddress}"), serializer = String.serializer()) ?: ""
  )

  val onlyRestArbeitenState = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyRestArbeiten}"), serializer = Boolean.serializer()) == true
  )

  val onlyEigenmontageState = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyEigenmontage}"), serializer = Boolean.serializer()) == true
  )

  val onlyDachmontageState = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyDachmontage}"), serializer = Boolean.serializer()) == true
  )

  val onlyElektroinstallationState = useState(
    LocalStorageSupport.loadFromLocalStorage(key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyElektroinstallation}"), serializer = Boolean.serializer()) == true
  )

  val sortedByFunction = useState(
    SortedByFunction(
      sortedBy = sortedBy,
      tableDataColumn = ServerProjectPreviewsTable.getProcessStatesDatesColumn(filteredProcessState = filteredByProjectState.value, userResolver = userResolver),
    )
  )

  val pageSize: StateInstance<Int?> = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.pageSize}"),
      serializer = Int.serializer()
    ) ?: 15
  )
  val currentPage = useState(
    LocalStorageSupport.loadFromLocalStorage(
      key = LocalStorageKey("${keyPrefix}${LocalTableStorageKeys.currentPage}"),
      serializer = Int.serializer()
    ) ?: 0
  )

  val visibleProjects: StateInstance<List<ResolvedProjectPreview>?> = useState(null)
  val numberOfFilteredProjects: StateInstance<Int?> = useState(null)
  val numberOfRelevantProjects: StateInstance<Int?> = useState(null)


  val updateFilteredProjects = useRef(
    debounce<ProjectQueryRequest>(coroutineScope = AppScope) { request ->
      visibleProjects.setter(null)
      numberOfFilteredProjects.setter(null)
      numberOfRelevantProjects.setter(null)

      UiActions.queryProjectPreviews(request) { response ->
        val projectQueryData = response?.data
        visibleProjects.setter(projectQueryData?.visibleProjectPreviews)
        numberOfFilteredProjects.setter(projectQueryData?.numberOfTotalFilteredProjects)
        numberOfRelevantProjects.setter(projectQueryData?.numberOfTotalRelevantProjects)
        currentPage.setter { old ->
          val newCurrentPage = projectQueryData?.numberOfTotalFilteredProjects?.divFloor(pageSize.value ?: 1)
          if (newCurrentPage == old) return@setter old
          old.coerceIn(0, newCurrentPage)
        }
      }
    }
  )

  useMemo(
    processStates,
    processStatesToFilter,
    processStatesToHide,
    filteredByCompanyCode.value,
    filteredByMaintainerName.value,
    filteredByEditorName.value,
    filterInputStateProject.value,
    filterInputStateAddress.value,
    filteredByProjectState.value,
    onlyRestArbeitenState.value,
    onlyEigenmontageState.value,
    onlyDachmontageState.value,
    onlyElektroinstallationState.value,
    sortedByFunction.value,
    pageSize.value,
    currentPage.value,
  ) {
    val sorting = ProjectQuerySorting(
      sortedBy = when (sortedByFunction.value.sortedBy) {
        SortedBy.Unsorted -> ProjectQuerySortedBy.Unsorted
        SortedBy.SortedAscending -> ProjectQuerySortedBy.SortedAscending
        SortedBy.SortedDescending -> ProjectQuerySortedBy.SortedDescending
      },
      sortedColumn = ServerProjectPreviewsTable.parseTableColumnId(sortedByFunction.value.sortedColumn.id),
      projectQueryForUser = projectQueryForUser,
      loggedInUser = loggedInUser.loginName,
    )

    val request = ProjectQueryRequest(
      processStatesToFilter = processStatesToFilter,
      processStatesToHide = processStatesToHide,
      filteredByCompanyCode = filteredByCompanyCode.value,
      filteredByMaintainer = filteredByMaintainerName.value,
      filteredByEditor = filteredByEditorName.value,
      filterValueProject = filterInputStateProject.value,
      filterValueAddress = filterInputStateAddress.value,
      filterByProjectState = filteredByProjectState.value,
      onlyRestarbeiten = onlyRestArbeitenState.value,
      onlyEigenmontage = onlyEigenmontageState.value,
      onlyDachmontage = onlyDachmontageState.value,
      onlyElektroinstallation = onlyElektroinstallationState.value,
      indexOfFirstVisibleProject = pageSize.value?.let { currentPage.value * it } ?: 0,
      indexOfLastVisibleProject = pageSize.value?.let { (currentPage.value + 1) * it } ?: Int.MAX_VALUE,
      sorting = sorting,
      projectQueryComponent = projectQueryComponent,
      includeArchived = includeArchived,
    )

    updateFilteredProjects.current?.let { it(request) }
  }


  div {

    projectsFiltering(
      filteredByCompanyCode = filteredByCompanyCode,
      filteredByMaintainerName = filteredByMaintainerName,
      filteredByEditorName = filteredByEditorName,
      filteredByProjectState = filteredByProjectState,
      processStatesToFilter = processStatesToFilter,
      filterInputStateProject = filterInputStateProject,
      filterInputStateAddress = filterInputStateAddress,
      localStorageKeyPrefix = keyPrefix,
    )

    div("mb-3") {
      span("me-4") {
        checkbox(
          value = onlyRestArbeitenState.value,
          onChange = {
            onlyRestArbeitenState.setter(it)
            LocalStorageSupport.saveToLocalStorage(
              key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyRestArbeiten}"),
              value = it,
              serializer = Boolean.serializer(),
            )
          },
          fieldName = "onlyRestArbeiten",
          title = "Restarbeiten",
          labelConfig = {
            span("badge rounded-pill bg-danger ms-2") {
              i(FontAwesomeIcons.PersonDigging) {}
            }
          }
        )
      }
      span("me-4") {
        checkbox(
          value = onlyEigenmontageState.value,
          onChange = {
            onlyEigenmontageState.setter(it)
            LocalStorageSupport.saveToLocalStorage(
              key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyEigenmontage}"),
              value = it,
              serializer = Boolean.serializer(),
            )
          },
          fieldName = "onlyEigenmontage",
          title = "Eigenmontage",
          labelConfig = {
            span("badge rounded-pill bg-dark ms-2") {
              i(FontAwesomeIcons.ScrewDriverWrench) {}
            }
          }
        )
      }
      span("me-4") {
        checkbox(
          value = onlyDachmontageState.value,
          onChange = {
            onlyDachmontageState.setter(it)
            LocalStorageSupport.saveToLocalStorage(
              key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyDachmontage}"),
              value = it,
              serializer = Boolean.serializer(),
            )
          },
          fieldName = "onlyDachmontage",
          title = "Dachmontage",
          labelConfig = {
            span("badge rounded-pill bg-info ms-2") {
              i(FontAwesomeIcons.SolarPanel) {}
            }
          }
        )
      }
      span("me-4") {
        checkbox(
          value = onlyElektroinstallationState.value,
          onChange = {
            onlyElektroinstallationState.setter(it)
            LocalStorageSupport.saveToLocalStorage(
              key = LocalStorageKey("${keyPrefix}${LocalStorageKeys.onlyElektroinstallation}"),
              value = it,
              serializer = Boolean.serializer(),
            )
          },
          fieldName = "onlyElektroinstallation",
          title = "Elektroinstallation",
          labelConfig = {
            span("badge rounded-pill bg-warning ms-2") {
              i(FontAwesomeIcons.Bolt) {}
            }
          }
        )
      }
    }

    busyIfNull(visibleProjects.value) { loadedProjectPreviews ->
      remoteTableWithPagination(
        entries = loadedProjectPreviews,
        columns = ServerProjectPreviewsTable.buildColumns(tableColumns = tableColumns(filteredByProjectState.value), navigate = navigate, projectQueryComponent = projectQueryComponent, loggedInUser = loggedInUser),
        pageSize = pageSize,
        currentPage = currentPage,
        numberOfTotalFilteredProjects = numberOfFilteredProjects.value ?: 0,
        sortedByFunction = sortedByFunction,
        keyPrefix = keyPrefix,
      )
    }

    div("mt-1") {
      span("form-text") {
        +"${numberOfFilteredProjects.value ?: "-"} von ${numberOfRelevantProjects.value ?: "-"} Projekten ausgewählt"
      }
    }

  }

}

external interface ProjectsOverview4Props : Props {
  var processStatesToFilter: List<LizergyProcessStates>
  var processStatesToHide: List<LizergyProcessStates>
  var projectQueryComponent: ProjectQueryComponent?
  var tableColumns: (filteredProcessState: LizergyProcessStates?) -> List<TableColumn<ServerProjectPreview>>
  var defaultFilteredBy: DefaultFilteredBy?
  var sortedBy: SortedBy
  var projectQueryForUser: ProjectQueryForUser
  var includeArchived: Boolean
  var keyPrefix: LocalStorageKeyPrefix
}
